import React, { useState, useCallback } from 'react';
import { generateProPhotos } from '../services/geminiService';
import { Spinner } from './Spinner';

export const ImageGenerator = ({ uploadedFiles, generatedImages, setGeneratedImages, onEditImage, onCreateVideo }) => {
  const [numPhotos, setNumPhotos] = useState(2);
  const [prompt, setPrompt] = useState('A professional product shot of the item on a clean, minimalist background with soft lighting.');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Helper functions for view type display
  const getViewIcon = (viewType) => {
    const icons = {
      front: '🔄',
      back: '🔄',
      left: '⬅️',
      right: '➡️',
      closeup: '🔍',
      lifestyle: '👕',
      detail: '📸',
      other: '📎'
    };
    return icons[viewType] || '📎';
  };

  const getDisplayLabel = (fileObj) => {
    if (fileObj.customLabel) return fileObj.customLabel;
    const labels = {
      front: 'Front View',
      back: 'Back View',
      left: 'Left Side',
      right: 'Right Side',
      closeup: 'Close Up',
      lifestyle: 'Lifestyle',
      detail: 'Detail',
      other: 'Other View'
    };
    return labels[fileObj.viewType] || 'Unknown View';
  };

  const handleGenerate = useCallback(async () => {
    if (!prompt.trim()) {
      setError("Please enter a prompt to generate images.");
      return;
    }
    if (!uploadedFiles || uploadedFiles.length === 0) {
      setError("Please upload at least one reference image.");
      return;
    }
    
    setIsLoading(true);
    setError(null);
    setGeneratedImages([]);
    try {
      // Files are already converted to base64 in App component, just use them directly
      const base64Files = uploadedFiles.map(fileObj => ({
        base64: fileObj.base64,
        mimeType: fileObj.mimeType
      }));
      
      const images = await generateProPhotos(base64Files, numPhotos, prompt);
      setGeneratedImages(images);
    } catch (err) {
      setError('Failed to generate images. Please try again.');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, [uploadedFiles, numPhotos, prompt, setGeneratedImages]);

  return (
    <div className="bg-gray-800 p-6 rounded-lg shadow-xl">
      {/* Reference Images Preview */}
      {uploadedFiles && uploadedFiles.length > 0 && (
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-white font-medium">Reference Images ({uploadedFiles.length})</h3>
            <div className="text-sm text-gray-400">
              {uploadedFiles.filter(f => f.viewType === 'front').length > 0 && '🔄 Front '}
              {uploadedFiles.filter(f => f.viewType === 'back').length > 0 && '🔄 Back '}
              {uploadedFiles.filter(f => f.viewType === 'left').length > 0 && '⬅️ Left '}
              {uploadedFiles.filter(f => f.viewType === 'right').length > 0 && '➡️ Right '}
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {uploadedFiles.map((fileObj, index) => (
              <div key={index} className="relative group">
                <img 
                  src={fileObj.base64}
                  alt={getDisplayLabel(fileObj)}
                  className="w-full h-24 object-cover rounded-lg transition-transform duration-200 group-hover:scale-105"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-70 text-white text-xs p-1 rounded-b-lg text-center">
                  <div className="flex items-center justify-center gap-1">
                    <span>{getViewIcon(fileObj.viewType)}</span>
                    <span className="truncate">{getDisplayLabel(fileObj)}</span>
                  </div>
                </div>
                {index === 0 && (
                  <div className="absolute top-1 left-1 bg-green-500 text-white text-xs px-1 rounded text-xs">
                    Primary
                  </div>
                )}
              </div>
            ))}
          </div>
          
          {/* Reference Summary */}
          {uploadedFiles.length > 1 && (
            <div className="mt-3 p-3 bg-blue-900 bg-opacity-20 rounded-lg border border-blue-700 border-opacity-30">
              <p className="text-sm text-blue-200 text-center">
                <strong>AI Context:</strong> Using {uploadedFiles.length} reference images including{' '}
                {uploadedFiles.map((file, index) => (
                  <span key={index}>
                    {getDisplayLabel(file).toLowerCase()}
                    {index < uploadedFiles.length - 2 ? ', ' : index === uploadedFiles.length - 2 ? ' and ' : ''}
                  </span>
                ))}
              </p>
            </div>
          )}
        </div>
      )}

      <div className="flex flex-col gap-4 mb-6">
        <div>
          <label htmlFor="prompt" className="block font-medium text-gray-300 mb-2">
            Generation Prompt:
            <span className="text-xs text-gray-400 ml-2">(Describe what you want to generate)</span>
          </label>
          <textarea
            id="prompt"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="e.g., Generate professional product photos showing front, back, and side views of this dress on a model with studio lighting"
            className="w-full bg-gray-700 border border-gray-600 text-white text-sm rounded-lg focus:ring-indigo-500 focus:border-indigo-500 block p-2.5 min-h-[80px] resize-y"
            disabled={isLoading}
            rows="3"
          />
          <div className="flex justify-between text-xs text-gray-400 mt-1">
            <span>Tip: Be specific about angles, background, and style</span>
            <span>{prompt.length}/500</span>
          </div>
        </div>
        
        <div className="flex flex-col sm:flex-row items-center gap-4">
          <div className="flex items-center gap-3">
            <label htmlFor="numPhotos" className="font-medium text-gray-300 whitespace-nowrap">
              Number of variants:
            </label>
            <select
              id="numPhotos"
              value={numPhotos}
              onChange={(e) => setNumPhotos(parseInt(e.target.value, 10))}
              className="bg-gray-700 border border-gray-600 text-white text-sm rounded-lg focus:ring-indigo-500 focus:border-indigo-500 block w-20 p-2.5"
              disabled={isLoading}
            >
              <option value="2">2</option>
              <option value="3">3</option>
              <option value="4">4</option>
              <option value="6">6</option>
              <option value="8">8</option>
            </select>
          </div>
          
          <button
            onClick={handleGenerate}
            disabled={isLoading || !uploadedFiles || uploadedFiles.length === 0}
            className="w-full sm:w-auto bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-900 disabled:cursor-not-allowed text-white font-bold py-2.5 px-6 rounded-lg transition duration-300 ease-in-out transform hover:scale-105 flex items-center justify-center min-w-[160px]"
          >
            {isLoading ? (
              <>
                <Spinner className="w-5 h-5 mr-2" />
                Generating...
              </>
            ) : (
              <>
                <span className="mr-2">✨</span>
                Generate Photos
              </>
            )}
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-900 bg-opacity-20 border border-red-700 rounded-lg">
          <p className="text-red-400 text-center">{error}</p>
        </div>
      )}

      {/* Generated Images Grid */}
      <div className="mt-8">
        {generatedImages.length > 0 && !isLoading && (
          <h3 className="text-white font-medium mb-4 text-lg border-b border-gray-700 pb-2">
            🎨 Generated Images ({generatedImages.length})
          </h3>
        )}
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-4">
          {isLoading && Array.from({ length: numPhotos }).map((_, index) => (
            <div key={index} className="aspect-square bg-gray-700 rounded-lg flex items-center justify-center animate-pulse border border-gray-600">
              <div className="text-center">
                <Spinner className="w-10 h-10 text-gray-500 mx-auto mb-2" />
                <p className="text-gray-400 text-sm">Generating image {index + 1}</p>
              </div>
            </div>
          ))}
          
          {!isLoading && generatedImages.length > 0 && generatedImages.map((image, index) => (
            <div key={image.id} className="group relative aspect-square bg-gray-900 rounded-lg overflow-hidden border border-gray-700 hover:border-indigo-500 transition-all duration-300">
              <img 
                src={image.base64} 
                alt={`Generated product ${index + 1}`} 
                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
              />
              
              {/* Image number badge */}
              <div className="absolute top-2 left-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded-full">
                #{index + 1}
              </div>
              
              {/* Hover overlay with actions */}
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-70 transition-all duration-300 rounded-lg flex items-center justify-center gap-2 px-2">
                <button
                  onClick={() => onEditImage(image)}
                  className="opacity-0 group-hover:opacity-100 bg-white/90 text-black font-bold py-2 px-3 rounded-lg transition-all duration-300 scale-90 group-hover:scale-100 text-sm hover:bg-white shadow-lg"
                >
                  ✏️ Edit
                </button>
                
                 <button
                  onClick={() => onCreateVideo(image)}
                  className="opacity-0 group-hover:opacity-100 bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 px-3 rounded-lg transition-all duration-300 scale-90 group-hover:scale-100 text-sm shadow-lg"
                >
                  🎬 Video
                </button>
                <a
                  href={image.base64}
                  download={`ai-product-photo-${image.id.substring(0, 8)}.png`}
                  className="opacity-0 group-hover:opacity-100 bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-3 rounded-lg transition-all duration-300 scale-90 group-hover:scale-100 text-sm shadow-lg inline-block"
                >
                  ⬇️ Download
                </a>
              </div>
            </div>
          ))}
        </div>

        {/* Empty state when no images generated yet */}
        {!isLoading && generatedImages.length === 0 && (
          <div className="text-center py-12">
            <div className="w-20 h-20 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">🎨</span>
            </div>
            <h3 className="text-white font-medium text-lg mb-2">Ready to Generate</h3>
            <p className="text-gray-400 max-w-md mx-auto">
              Click the "Generate Photos" button to create AI-powered product images using your reference photos.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};